import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { Associate } from '../../../types/associates';
import { AcademiaResponse } from '../../../types/common';
import { Service, ServiceReorder } from '../../../types/services';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useServiceActions = (
  whichAction:
    | 'serviceList'
    | 'serviceWiseAssociateList'
    | 'service&associate'
    | '',
  serviceId?: number
) => {
  // query function to fetch service list from database declared here
  const getServiceListHandler = useQuery({
    queryKey: ['service-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Service>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        '/service/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'serviceList' || whichAction === 'service&associate'
    ),
  });

  const getServiceWiseAssociatesHandler = useQuery({
    queryKey: ['service-wise-associates', serviceId],
    queryFn: async (): Promise<AcademiaResponse<Array<Associate>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        `/service/wise/associates/${serviceId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'serviceWiseAssociateList' ||
        (whichAction === 'service&associate' && !!serviceId)
    ),
  });

  // mutation function to create new service into database declared here
  const createNewServiceHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: FormData;
    }): Promise<AcademiaResponse<Service>> => {
      const response = await getPrivateAxiosInstance('appointment').post(
        '/service/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing service into database declared here
  const updateServiceHandler = useMutation({
    mutationFn: async ({
      serviceId,
      body,
    }: {
      serviceId: number;
      body: FormData;
    }): Promise<AcademiaResponse<Service>> => {
      const response = await getPrivateAxiosInstance('appointment').patch(
        `/service/${serviceId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to update service popularity order declared here
  const updateServicePopularityHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: Array<ServiceReorder>;
    }): Promise<AcademiaResponse<Array<Service>>> => {
      const response = await getPrivateAxiosInstance('appointment').patch(
        '/service/bulk/update/',
        body
      );

      return response.data;
    },
  });

  // mutation function to delete service by id from the database declared here
  const deleteServiceByIdHandler = useMutation({
    mutationFn: ({
      serviceId,
    }: {
      serviceId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('appointment').delete(
        `/service/${serviceId}/delete/`
      ),
  });

  return {
    getServiceListHandler,
    getServiceWiseAssociatesHandler,
    createNewServiceHandler,
    updateServiceHandler,
    updateServicePopularityHandler,
    deleteServiceByIdHandler,
  };
};

export default useServiceActions;
